/**
 * TCX-824: single source of truth for writing the ACCESS token to
 * localStorage, plus the cross-tab "account switch" reload.
 *
 * THE PROBLEM. localStorage is shared across every tab of the origin. When
 * another tab logs out and signs in as a DIFFERENT account, an already-open
 * tab keeps rendering the OLD account's React state, while the axios request
 * interceptor (client/axiosInstance.ts) reads the LATEST stored access token
 * for every request. A later user click in the stale tab is therefore sent
 * under the NEW account — old-account UI, new-account credentials. The
 * cross-account REPLAY vector inside the refresh flow itself was already
 * closed in TCX-795 (same-subject recovery guard in axiosInstance.ts); this
 * module closes the remaining "stale tab keeps clicking" vector by reloading
 * the stale tab as soon as the stored token's subject changes, so it
 * re-initializes under the account that now owns the session.
 *
 * HOW IT WORKS.
 *  - `trackedSubject` is the JWT `sub` this tab is currently RENDERING as.
 *    It initializes from the stored access token at module load and is kept
 *    current by `setAccessToken`.
 *  - `initCrossTabAccountSwitchReload` listens for `storage` events on the
 *    `accessToken` key and reloads the page ONLY when the newly stored token
 *    decodes to a non-null subject that DIFFERS from `trackedSubject`.
 *
 * WHAT DELIBERATELY DOES **NOT** RELOAD:
 *  - Same-subject rotation: another tab refreshed the SAME user's token
 *    (multi-tab refresh contention, see TCX-795). Reloading here was exactly
 *    the spurious-reload bug that got a previous cut of this fix reverted.
 *  - `newValue` null/cleared: a logout in another tab. The withAuth /
 *    eviction flows already handle signed-out state, and never reloading on
 *    null also prevents reload loops. IMPORTANT: we do NOT null out
 *    `trackedSubject` on that event — `trackedSubject` describes what this
 *    tab is RENDERING, which is still the old account. That way a subsequent
 *    DIFFERENT-user login still triggers a reload, while a SAME-user re-login
 *    does not.
 *  - An undecodable/malformed token: unknown subject, treat as no-op rather
 *    than reload on garbage.
 *
 * WHY EVERY SAME-TAB WRITE MUST GO THROUGH `setAccessToken`. `storage` events
 * do NOT fire in the tab that performed the write — only in the other tabs.
 * So the writing tab can only keep `trackedSubject` current by updating it at
 * the moment it writes. If some path wrote `localStorage.setItem(
 * 'accessToken', ...)` directly (e.g. a same-tab login as a different user),
 * `trackedSubject` would go stale and a later BENIGN cross-tab event (a
 * same-user rotation of the new account) would look like a subject change and
 * spuriously reload. Hence `setAccessToken` is the ONLY sanctioned way to
 * write the access token; a lint-friendly grep for `setItem('accessToken'`
 * should only ever hit this file.
 *
 * NOTE: this module must NOT import from axiosInstance.ts (axiosInstance
 * imports us — an import in the other direction would create a cycle).
 */

/**
 * TCX-795 (security, cross-account replay): decode the subject (`sub`) claim
 * from a JWT access token WITHOUT verifying its signature — we only need to
 * know which user an already-issued token belongs to, not to trust it.
 *
 * The backend signs `{ sub: user.id, ... }` (auth.service / jwt.service), so
 * `sub` is the authoritative user id. We mirror the safe, throw-free decode
 * already used in hooks/useAuthValidation.ts (base64url payload -> JSON)
 * rather than pulling that React hook module into the axios client. Any
 * malformed / missing token yields `null` so callers treat it as "unknown
 * subject".
 *
 * (Moved here from axiosInstance.ts in TCX-824; axiosInstance re-exports it
 * so existing importers keep working.)
 */
export const decodeTokenSub = (token: string | null | undefined): string | null => {
  if (!token) return null;
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;
    // Normalise base64url -> base64 before decoding.
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub.length > 0
      ? payload.sub
      : null;
  } catch {
    return null;
  }
};

/**
 * The subject this tab is currently RENDERING as (see header comment).
 * Initialized from whatever token was in storage when the app booted.
 */
let trackedSubject: string | null =
  typeof window !== 'undefined'
    ? decodeTokenSub(window.localStorage.getItem('accessToken'))
    : null;

/**
 * TCX-824: the ONLY sanctioned way to write the access token. Persists the
 * token AND records its subject as what this tab now renders as, so the
 * cross-tab listener below can tell a benign same-user rotation from a real
 * account switch. (The `storage` event never fires in the writing tab, so
 * this is the writing tab's only chance to stay current.)
 */
export const setAccessToken = (token: string): void => {
  localStorage.setItem('accessToken', token);
  trackedSubject = decodeTokenSub(token);
};

/**
 * Stable, named handler so add/removeEventListener reference the same fn
 * (mirrors components/app/globalErrorHandlers.ts).
 */
function handleAccessTokenStorageEvent(event: StorageEvent): void {
  // Only the access-token key is of interest; ignore refreshToken/csrf/etc.
  if (event.key !== 'accessToken') return;

  const newSubject = decodeTokenSub(event.newValue);

  // Cleared (cross-tab logout) or undecodable: never reload, and do NOT
  // touch trackedSubject — this tab still renders the old account (see
  // header comment for why that matters).
  if (newSubject === null) return;

  // This tab is not rendering any account (booted with no access token — a
  // sign-in / sign-up / activation page). There is no stale account to protect,
  // so a login in another tab must NOT reload us: doing so would blow away
  // multi-step sign-up state that lives only in React state (see
  // pages/sign-up/index.tsx). Leave trackedSubject null so a later real switch
  // (once this tab is actually rendering an account) still reloads.
  if (trackedSubject === null) return;

  // Same-user rotation (another tab refreshed the same account's token):
  // benign, no reload — reloading here is the exact regression that got the
  // previous cut of this fix reverted.
  if (newSubject === trackedSubject) return;

  // A DIFFERENT account now owns the session. This tab's React state is
  // stale and its next click would be sent under the new account's token —
  // reload so the tab re-initializes as the account that owns the session.
  window.location.reload();
}

/** Module flag guarding idempotent registration (double-init = no-op). */
let crossTabReloadRegistered = false;

/**
 * TCX-824: register the cross-tab account-switch reload listener. Idempotent
 * — calling it again while already registered does nothing, so a StrictMode
 * double-invoke or repeated bootstrap can't stack listeners.
 */
export const initCrossTabAccountSwitchReload = (): void => {
  if (typeof window === 'undefined') return;
  if (crossTabReloadRegistered) return;

  window.addEventListener('storage', handleAccessTokenStorageEvent);
  crossTabReloadRegistered = true;
};

/**
 * Detach the listener and clear the registration flag. Exported so tests can
 * clean up between cases; also invoked from the HMR dispose hook below so a
 * Vite hot reload swaps the listener instead of accumulating another one.
 */
export const unregisterCrossTabAccountSwitchReload = (): void => {
  if (typeof window === 'undefined') return;
  if (!crossTabReloadRegistered) return;

  window.removeEventListener('storage', handleAccessTokenStorageEvent);
  crossTabReloadRegistered = false;
};

// Before Vite hot-swaps this module, tear down the live listener so the fresh
// module instance re-registers exactly one — no accumulation.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unregisterCrossTabAccountSwitchReload();
  });
}
